import { toStructuredFormulation } from '@/modules/formulary/utils/formulationString';
import { proposedChangeLines } from '@/modules/formulary/utils/opportunity';
import { buildAnalysisSummary } from './analysisFixtures';
import { buildFormulationRows } from './investigationFixtures';

// The four opportunities the demo shipped with, as the local store's starting
// data: same medications, statuses, titles, people, dates and scope.
//
// Fixture rows are hashed from the scope, so the demo's own formulations don't
// exist here; each seed's sections are built from buildFormulationRows for its
// scope instead, so it reopens in Analyze with nothing missing. Epinephrine has
// only one mg/mL row for adults in the fixtures, so its seed covers all
// populations to have something to change.

const SEED_SCOPE = {
  startDate: '2025-07-13',
  endDate: '2026-07-13',
  quickDateRange: 'custom',
  facilities: [],
  patientPopulations: ['ADULT'],
  minInfusions: 10,
};

const DEMO_USER = 'morgan@example.com';

// Fixture rows mix units under one drug; keep proposals within mg/mL.
const unitOf = (formulation) => toStructuredFormulation(formulation)?.unit || '';

const savedRow = (row) => ({ formulation: row.formulation, infusions: row.infusions, costPerInfusion: null });

// Targets the drug's most-used mg/mL row, never an RTU product: there is no RTU
// source outside fixtures yet. The target's own row rides along locked, as
// autoMatchTarget would add it.
function planAnalysis(medicationName, scope, moves) {
  const rows = buildFormulationRows({ ...scope, medicationName }).filter((row) => row.infusions >= scope.minInfusions);
  const candidates = rows.filter((row) => unitOf(row.formulation) === 'mg/mL');
  const [targetRow] = candidates;
  if (!targetRow) return { analysis: { sections: [], unchangedFormulations: rows.map(savedRow) }, changes: [] };

  const switching = candidates.filter((row) => row !== targetRow).slice(0, moves);
  const inSection = new Set([targetRow, ...switching]);

  return {
    analysis: {
      sections: [
        {
          targetFormulation: targetRow.formulation,
          targetRtuCategory: null,
          targetCost: null,
          splitMode: 'single',
          targetFormulation2: '',
          targetCost2: null,
          rows: [targetRow, ...switching].map(savedRow),
        },
      ],
      unchangedFormulations: rows.filter((row) => !inSection.has(row)).map(savedRow),
    },
    changes: switching.map((row) => ({
      currentFormulation: row.formulation,
      updatedFormulation: targetRow.formulation,
      current: toStructuredFormulation(row.formulation),
      updated: toStructuredFormulation(targetRow.formulation),
    })),
  };
}

function buildSeed({ id, medicationName, moves, scopeOverrides, ...fields }) {
  const scope = { ...SEED_SCOPE, ...scopeOverrides, medicationName };
  const { analysis, changes } = planAnalysis(medicationName, scope, moves);
  return {
    id,
    medicationName,
    title: `Optimize ${medicationName.charAt(0).toUpperCase()}${medicationName.slice(1)} Formulation(s)`,
    description: '',
    createdBy: DEMO_USER,
    owner: DEMO_USER,
    notes: [],
    scope: {
      facilities: scope.facilities,
      patientPopulations: scope.patientPopulations,
      minInfusions: scope.minInfusions,
      dateRange: { start: scope.startDate, end: scope.endDate, quickRange: scope.quickDateRange },
    },
    concentrationUnit: null,
    analysis,
    proposedChanges: proposedChangeLines(analysis.sections),
    estimatedImpacts: buildAnalysisSummary(scope, changes),
    ...fields,
  };
}

const SEEDS = [
  {
    id: 'opp-seed-midazolam',
    medicationName: 'midazolam',
    moves: 1,
    status: 'active',
    concentrationUnit: 'mg',
    createdAt: '2026-07-17T15:01:25.687Z',
    updatedAt: '2026-07-17T15:01:40.000Z',
  },
  {
    id: 'opp-seed-norepinephrine',
    medicationName: 'norepinephrine',
    moves: 1,
    status: 'saved',
    createdAt: '2026-07-17T15:03:24.398Z',
    updatedAt: '2026-07-17T15:03:24.398Z',
  },
  {
    id: 'opp-seed-epinephrine',
    medicationName: 'epinephrine',
    moves: 2,
    scopeOverrides: { patientPopulations: [] },
    status: 'recommended',
    createdAt: '2026-07-17T15:04:44.544Z',
    updatedAt: '2026-07-17T15:05:05.000Z',
  },
  {
    id: 'opp-seed-nicardipine',
    medicationName: 'nicardipine',
    moves: 1,
    status: 'saved',
    createdAt: '2026-07-17T15:06:42.980Z',
    updatedAt: '2026-07-17T15:06:42.980Z',
  },
];

export function buildOpportunitySeeds() {
  return SEEDS.map(buildSeed);
}

export default buildOpportunitySeeds;
